import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  unique,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";

/** ฟอร์ม public / admin — Draft → Published → Closed */
export const formStatuses = ["draft", "published", "closed"] as const;
export type FormStatus = (typeof formStatuses)[number];

export const formFieldTypes = [
  "text",
  "number",
  "textarea",
  "choice",
  "checkbox",
  "dropdown",
  "date",
  "datetime",
  "file",
  "rating",
  "repeater",
  "section",
] as const;
export type FormFieldType = (typeof formFieldTypes)[number];

/** สุ่มรางวัลตอนส่งฟอร์ม — flow เดียวกับ visitor lottery ของ Summer camp */
export type FormLotterySettings = {
  enabled: boolean;
  /** field ที่ใช้เป็นชื่อผู้ลงทะเบียน */
  nameFieldId: string;
  /** field ชื่อเล่น (ไม่บังคับ) */
  nicknameFieldId?: string | null;
  /** field ที่ห้ามซ้ำต่อฟอร์ม (เบอร์โทร) */
  uniqueFieldId: string;
  successMessage?: string | null;
  missMessage?: string | null;
};

/** เก็บใน `forms.settings` (jsonb) */
export type FormSettings = {
  /** ISO 8601 — ปิดรับหลังเวลานี้ */
  deadline?: string;
  /** จำกัดจำนวนคำตอบทั้งฟอร์ม */
  maxResponses?: number;
  /** object key ใน bucket — แบนเนอร์ด้านบนหน้า public */
  posterKey?: string;
  /** bcrypt hash — ບໍ່ສົ່ງໃຫ້ client */
  viewerPinHash?: string;
  viewerPinSetAt?: string;
  /** ISO 8601 — ລິ້ງເບິ່ງຄຳຕອບໃຊ້ບໍ່ໄດ້ຫຼັງເວລານີ້ (ບໍ່ຕັ້ງ = ບໍ່ຫມົດອາຍຸ) */
  viewerAccessExpiresAt?: string;
  lottery?: FormLotterySettings;
};

export const formLotteryDrawModes = ["instant", "deferred"] as const;
export type FormLotteryDrawMode = (typeof formLotteryDrawModes)[number];

export const formLotteryDrawKinds = ["instant", "deferred"] as const;
export type FormLotteryDrawKind = (typeof formLotteryDrawKinds)[number];

export type FormLotteryDrawEligiblePrize = {
  id: string;
  remaining: number;
  weight: number;
};

/** เก็บใน `form_fields.options` สำหรับ choice / dropdown / checkbox */
export type FormFieldOptionItem = {
  value: string;
  label: string;
  /** false = ປິດຮັບເລືອກໃນຟອມ public ແຕ່ຍັງເກັບໄວ້ (ຄຳຕອບເກົ່າຍັງໃຊ້ label ໄດ້) */
  enabled?: boolean;
};
export type FormFieldOptions = FormFieldOptionItem[];

/** sub-field types ທີ່ອະນຸຍາດໃນ repeater item */
export const repeaterSubFieldTypes = [
  "text",
  "number",
  "date",
  "choice",
] as const;
export type RepeaterSubFieldType = (typeof repeaterSubFieldTypes)[number];

export const repeaterCountModes = ["fixed", "range", "dynamic"] as const;
export type RepeaterCountMode = (typeof repeaterCountModes)[number];

export type RepeaterItemFieldDef = {
  key: string;
  label: string;
  type: RepeaterSubFieldType;
  required?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  integerOnly?: boolean;
  pattern?: string;
  options?: FormFieldOptionItem[];
};

export type RepeaterSettings = {
  countMode: RepeaterCountMode;
  fixedCount?: number;
  minItems?: number;
  maxItems?: number;
  itemFields: RepeaterItemFieldDef[];
  itemLabelTemplate?: string;
  allowAddRemove?: boolean;
  showItemNumber?: boolean;
};

/**
 * เก็บใน `form_fields.settings` — ควรสัมพันธ์กับ `type` ของ field
 * (บังคับความสอดคล้องที่ layer API / Zod)
 */
export type FormFieldSettings = {
  maxRating?: 5 | 10;
  /** ຟິວປະເພດ number — ຂອບເຂດຄ່າ (ລວມຂອບ) */
  min?: number;
  max?: number;
  /** ຖ້າ true ຮັບສະເພາຍເລກທຳມະຊາດ (ບໍ່ມີເລກທົດສະນິຍົມ) */
  integerOnly?: boolean;
  accept?: readonly string[];
  maxFileSizeBytes?: number;
  minLength?: number;
  maxLength?: number;
  rows?: number;
  minDate?: string;
  maxDate?: string;
  use24Hour?: boolean;
  allowOther?: boolean;
  helperText?: string;
  /** repeater — ຈຳນວນແລະ schema ຂອງແຕ່ລະລາຍການ */
  countMode?: RepeaterCountMode;
  fixedCount?: number;
  minItems?: number;
  maxItems?: number;
  itemFields?: RepeaterItemFieldDef[];
  itemLabelTemplate?: string;
  allowAddRemove?: boolean;
  showItemNumber?: boolean;
};

export const formCategoryStatuses = ["active", "inactive"] as const;
export type FormCategoryStatus = (typeof formCategoryStatuses)[number];

export type FormFieldSettingsByType = {
  text: Pick<FormFieldSettings, "minLength" | "maxLength" | "helperText">;
  number: Pick<FormFieldSettings, "min" | "max" | "integerOnly" | "helperText">;
  textarea: Pick<
    FormFieldSettings,
    "minLength" | "maxLength" | "rows" | "helperText"
  >;
  choice: Pick<FormFieldSettings, "allowOther" | "helperText">;
  checkbox: Pick<FormFieldSettings, "allowOther" | "helperText">;
  dropdown: Pick<FormFieldSettings, "allowOther" | "helperText">;
  date: Pick<FormFieldSettings, "minDate" | "maxDate" | "helperText">;
  datetime: Pick<
    FormFieldSettings,
    "minDate" | "maxDate" | "use24Hour" | "helperText"
  >;
  file: Pick<FormFieldSettings, "accept" | "maxFileSizeBytes" | "helperText">;
  rating: Pick<FormFieldSettings, "maxRating" | "helperText">;
  repeater: Pick<
    FormFieldSettings,
    | "countMode"
    | "fixedCount"
    | "minItems"
    | "maxItems"
    | "itemFields"
    | "itemLabelTemplate"
    | "allowAddRemove"
    | "showItemNumber"
    | "helperText"
  >;
  section: Pick<FormFieldSettings, "helperText">;
};

/**
 * หมวดหมู่ฟอร์ม — lookup สำหรับจัดกลุ่มในรายการ admin
 */
export const formCategories = pgTable(
  "form_categories",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: text("name").notNull().unique(),
    description: text("description"),
    status: text("status", { enum: formCategoryStatuses })
      .notNull()
      .default("active"),
    order: integer("order"),
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("form_categories_by_status").on(t.status),
    index("form_categories_by_order").on(t.order),
  ],
);

/**
 * Form definitions (builder + lifecycle)
 */
export const forms = pgTable(
  "forms",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    title: text("title").notNull(),
    description: text("description"),
    categoryId: text("category_id").references(() => formCategories.id, {
      onDelete: "set null",
    }),
    status: text("status", { enum: formStatuses }).notNull().default("draft"),
    settings: jsonb("settings").$type<FormSettings>(),
    createdBy: text("created_by").references(() => user.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("forms_by_status").on(t.status),
    index("forms_by_created_by").on(t.createdBy),
    index("forms_by_category_id").on(t.categoryId),
  ],
);

/**
 * Field definitions per form — order for drag-sort
 */
export const formFields = pgTable(
  "form_fields",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "cascade" }),
    type: text("type", { enum: formFieldTypes }).notNull(),
    label: text("label").notNull(),
    description: text("description"),
    placeholder: text("placeholder"),
    required: boolean("required").notNull().default(false),
    /** choice / dropdown / checkbox */
    options: jsonb("options").$type<FormFieldOptions>(),
    settings: jsonb("settings").$type<FormFieldSettings>(),
    order: integer("order").notNull(),
    createdAt: timestamp("created_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("form_fields_by_form_order").on(t.formId, t.order),
    index("form_fields_by_form_id").on(t.formId),
  ],
);

/**
 * ຄຳຕອບຕໍ່ຟອມ — ອາດບໍ່ມີອີເມວ (ສົ່ງແບບ anonymous ໄດ້ຫຼາຍຄັ້ງ)
 */
export const formResponses = pgTable(
  "form_responses",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "cascade" }),
    respondentEmail: text("respondent_email"),
    submittedAt: timestamp("submitted_at", { withTimezone: false })
      .notNull()
      .defaultNow(),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
  },
  (t) => [
    index("form_responses_by_form_id").on(t.formId),
    index("form_responses_by_submitted_at").on(t.submittedAt),
  ],
);

/**
 * Answers per submission — restrict delete field while answers reference it
 */
export const formResponseAnswers = pgTable(
  "form_response_answers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    responseId: text("response_id")
      .notNull()
      .references(() => formResponses.id, { onDelete: "cascade" }),
    fieldId: text("field_id")
      .notNull()
      .references(() => formFields.id, { onDelete: "restrict" }),
    value: text("value"),
    fileKey: text("file_key"),
  },
  (t) => [
    unique("form_response_answers_response_id_field_id_unique").on(
      t.responseId,
      t.fieldId,
    ),
    index("form_response_answers_by_response_id").on(t.responseId),
    index("form_response_answers_by_field_id").on(t.fieldId),
  ],
);

export const formLotteryPrizes = pgTable(
  "form_lottery_prizes",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "cascade" }),
    name: text("name").notNull(),
    description: text("description"),
    imageUrl: text("image_url"),
    quantity: integer("quantity").notNull().default(0),
    remaining: integer("remaining").notNull().default(0),
    probabilityWeight: integer("probability_weight").notNull().default(1),
    isConsolation: boolean("is_consolation").notNull().default(false),
    drawMode: text("draw_mode", { enum: formLotteryDrawModes })
      .notNull()
      .default("instant"),
    redeemCodePrefix: text("redeem_code_prefix"),
    enabled: boolean("enabled").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("form_lottery_prizes_by_form").on(t.formId),
    index("form_lottery_prizes_by_form_enabled").on(t.formId, t.enabled),
    index("form_lottery_prizes_by_form_draw_mode").on(t.formId, t.drawMode),
  ],
);

export const formLotteryEntries = pgTable(
  "form_lottery_entries",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "cascade" }),
    responseId: text("response_id")
      .notNull()
      .references(() => formResponses.id, { onDelete: "cascade" }),
    uniqueKey: text("unique_key").notNull(),
    fullName: text("full_name").notNull(),
    nickname: text("nickname"),
    prizeId: text("prize_id").references(() => formLotteryPrizes.id, {
      onDelete: "set null",
    }),
    prizeNameSnapshot: text("prize_name_snapshot"),
    redeemCode: text("redeem_code"),
    redeemedAt: timestamp("redeemed_at", {
      mode: "string",
      withTimezone: false,
    }),
    redeemedBy: text("redeemed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    drawnAt: timestamp("drawn_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    submittedAt: timestamp("submitted_at", {
      mode: "string",
      withTimezone: false,
    })
      .notNull()
      .defaultNow(),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    unique("form_lottery_entries_form_unique_key").on(t.formId, t.uniqueKey),
    unique("form_lottery_entries_response_id").on(t.responseId),
    unique("form_lottery_entries_redeem_code").on(t.redeemCode),
    index("form_lottery_entries_by_form_submitted").on(t.formId, t.submittedAt),
  ],
);

export const formLotteryDrawLogs = pgTable(
  "form_lottery_draw_logs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    entryId: text("entry_id")
      .notNull()
      .references(() => formLotteryEntries.id, { onDelete: "cascade" }),
    formId: text("form_id")
      .notNull()
      .references(() => forms.id, { onDelete: "cascade" }),
    eligiblePrizeIds: jsonb("eligible_prize_ids")
      .$type<FormLotteryDrawEligiblePrize[]>()
      .notNull()
      .default([]),
    rolledValue: integer("rolled_value"),
    wonPrizeId: text("won_prize_id").references(() => formLotteryPrizes.id, {
      onDelete: "set null",
    }),
    drawKind: text("draw_kind", { enum: formLotteryDrawKinds })
      .notNull()
      .default("instant"),
    createdAt: timestamp("created_at", { mode: "string", withTimezone: false })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    index("form_lottery_draw_logs_by_form").on(t.formId),
    index("form_lottery_draw_logs_by_entry").on(t.entryId),
  ],
);
